import { GridApi } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.min.css' // Optional theme CSS
import { ObGrid } from '@outbound-ai/ursa'
import { apiPaths } from 'configs/apiConfigs'
import { FormatTime } from 'features/autocheck/utils'
import { exportPDF } from 'features/claims/claimsApi'
import { IClaim } from 'features/claims/types'
import { useEffect, useState } from 'react'
import { FaCaretDown, FaCaretUp, FaDownload, FaEye, FaSearch, FaTimes } from 'react-icons/fa'
import { Button, ObModal } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { SlotData } from 'utils/dataExtractor'
import { httpClaims } from 'utils/http'
import { ClaimLineItemColumnConfig } from './ClaimLineItemsColumnconfig'
import { EOBDownload } from './ClaimsLineItemUtils'

interface IClaimDetailServiceLineItemProps {
  claim: IClaim
  data?: SlotData[]
}

interface TotalLineItemType {
  serviceLineItemId: string
  serviceBilledAmount: number
  units: number
  placeOfService: string
  allowedCoveredAmount: number
  discount: number
  disallowedNotCoveredAmount: number
  patientBalance: number
  paidAmount: number
  deductible: number
  copay: number
  coInsurance: number
}

export const ClaimLineItems = ({ claim, data }: IClaimDetailServiceLineItemProps) => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [quickSearchValue, setQuickSearchValue] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)
  const [serviceLineItems, setServiceLineItems] = useState<undefined | []>()
  const [eobReceived, setEobReceived] = useState()
  const [eobId, setEobId] = useState('' as any)
  const [showModal, setShowModal] = useState(false)
  const [pdf, setPdf] = useState('' as any)
  const [fileName, setFileName] = useState('' as any)
  const [panelHeight, setPanelHeight] = useState(0 as any)
  const [lineItemTotal, setLineItemTotal] = useState({} as TotalLineItemType)

  useEffect(() => {
    const elmnt = document.getElementById('tabContent')
    setPanelHeight(elmnt?.clientHeight)
  }, [panelHeight])

  const handleShowModal = (data: any) => {
    setShowModal(true)
    exportPDF(data).then((res: any) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      setPdf(link.href + '#toolbar=1&navpanes=0&scrollbar=0&zoom=100%&view=fit')
    })
  }

  // API Call to get Service Line Items
  useEffect(() => {
    let isMounted = true
    if (claim) {
      httpClaims(apiPaths.serviceLineItems(claim.oaiClaimId)).then((res) => {
        if (isMounted && res.data) {
          const lineItemData = res.data.lineItems.filter((item: any) => item.lineItemType === 0)
          const lineItemTotal = res.data.lineItems.filter(
            (item: any) => item.lineItemType === 1 && item.lineItemId === 9999,
          )
          setLineItemTotal(lineItemTotal[0] as TotalLineItemType)
          const remarkData = lineItemData.map((item: any) => {
            const remarks = item.remarks.map((remark: any) => remark.code + ' ' + remark.description)
            const remarksData = remarks.join(', ')
            return {
              ...item,
              remarksData: remarksData,
            }
          })
          setServiceLineItems(remarkData)
          setEobReceived(res.data.created)
          setEobId(res.data.eobId)
          setFileName(res.data.fileName ? res.data.fileName : '')
        } else {
          setServiceLineItems([])
        }
      })
    }
    return () => {
      isMounted = false
    }
  }, [claim, setServiceLineItems])

  // Quick Search
  useEffect(() => {
    gridApi?.setQuickFilter(quickSearchValue)
  }, [quickSearchValue])

  // Search Functionality
  const onQuickSearchChange = (e: any) => {
    setQuickSearchValue(e.target.value)
    if (e.target.value === '') {
      gridApi?.setQuickFilter('')
    } else {
      gridApi?.setQuickFilter(e.target.value)
    }
  }

  // Update Aggregation row data
  useEffect(() => {
    if (lineItemTotal) {
      gridApi?.setPinnedBottomRowData([
        {
          dos: '',
          serviceCode: 'TOTAL',
          units: '',
          serviceBilledAmount: lineItemTotal?.serviceBilledAmount,
          allowedCoveredAmount: lineItemTotal?.allowedCoveredAmount,
          discount: lineItemTotal?.discount,
          disallowedNotCoveredAmount: lineItemTotal?.disallowedNotCoveredAmount,
          patientBalance: lineItemTotal?.patientBalance,
          paidAmount: lineItemTotal?.paidAmount,
          deductible: lineItemTotal?.deductible,
          copay: lineItemTotal?.copay,
          coInsurance: lineItemTotal?.coInsurance,
          placeOfService: '',
        },
      ])
    }
  }, [lineItemTotal, gridApi])

  // On grid ready
  function onGridReady(params: any): Promise<void> {
    if (!gridApi) {
      setGridApi(params.api)
    }
    params.api.sizeColumnsToFit()
    const updateColumnWidth = (field: string, width: number) => {
      const columnDefs = params.api.getColumnDefs()
      const updatedColumnDefs = columnDefs.map((colDef: any) => {
        if (colDef.field === field) {
          return { ...colDef, width }
        }
        return colDef
      })
      params.api.setColumnDefs(updatedColumnDefs)
    }
    if (window.innerWidth < 1700 && window.innerWidth > 1500) {
      updateColumnWidth('dos', 110)
      updateColumnWidth('paidAmount', 80)
      updateColumnWidth('billed', 75)
      updateColumnWidth('units', 70)
      updateColumnWidth('coInsurance', 70)
      updateColumnWidth('copay', 75)
      updateColumnWidth('disallowedNotCoveredAmount', 95)
      updateColumnWidth('deduct', 85)
      updateColumnWidth('placeOfService', 70)
      updateColumnWidth('serviceCode', 105)
      updateColumnWidth('patientBalance', 75)
      updateColumnWidth('discount', 90)
      updateColumnWidth('allowedCoveredAmount', 90)
      updateColumnWidth('lineItemId', 15)
    } else {
      updateColumnWidth('lineItemId', 15)
      updateColumnWidth('serviceCode', 195)
      updateColumnWidth('units', 70)
    }

    return Promise.resolve()
  }

  // detailed grid render config
  const detailCellRendererParams = {
    detailGridOptions: {
      columnDefs: [
        { field: 'code', headerName: 'Remark Code' },
        { field: 'description', headerName: 'Description', cellClass: 'desciption-cnt' },
      ],
      rowHeight: '20',
      defaultColDef: {
        autoHeight: true,
        resizable: true,
      },
      onGridReady: (params: any) => {
        const updateColumnWidth = (field: string, width: number) => {
          const columnDefs = params.api.getColumnDefs()
          const updatedColumnDefs = columnDefs.map((colDef: any) => {
            if (colDef.field === field) {
              return { ...colDef, width }
            }
            return colDef
          })
          params.api.setColumnDefs(updatedColumnDefs)
        }
        updateColumnWidth('code', -20)
        params.api.sizeColumnsToFit()
      },

      localeText: {
        noRowsToShow: 'No Remark Code Extracted',
      },
    },
    // get the rows for each Detail Grid
    getDetailRowData: (params: any) => {
      params.successCallback(params.data.remarks)
    },
  }

  // toogle detailed grid expand/collapse
  const toggleExpandAll = () => {
    setIsExpanded(!isExpanded)
    gridApi?.forEachNode(function (rowNode) {
      rowNode.setExpanded(isExpanded)
    })
  }

  return (
    <Div panelHeight={panelHeight}>
      {serviceLineItems && eobReceived && eobReceived !== null && eobReceived !== '' && (
        <>
          <div className="lineitem-extration">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto' }}>
              {data?.map((slotObject: SlotData) => (
                <div
                  key={slotObject.entId}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto auto',
                    gap: '0.5rem',
                  }}
                >
                  <div className="extracted-detail-label">{slotObject.description}</div>
                  <div className="extracted-detail-value">{slotObject.value}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lineitem-header">
            <div>
              <div className="search-container">
                <div className="search-icons">
                  {quickSearchValue ? <FaTimes onClick={() => setQuickSearchValue('')} /> : <FaSearch />}
                </div>
                <input
                  type="text"
                  placeholder="Search any service or remark content"
                  value={quickSearchValue}
                  onInput={onQuickSearchChange}
                />
              </div>
            </div>
            <div className="lineitem-settings">
              <div>{fileName != '' && <>EOB Last received on {FormatTime(eobReceived!)}</>}</div>
              <Button
                variant="secondary"
                size="small"
                radius="rounded"
                disabled={fileName === ''}
                onClick={() => handleShowModal(eobId)}
              >
                <FaEye />
              </Button>
              <Button
                variant="secondary"
                size="small"
                radius="rounded"
                disabled={fileName === ''}
                onClick={() => EOBDownload(eobId)}
              >
                <FaDownload />
              </Button>
              <Button onClick={toggleExpandAll} variant="tertiary" size="small">
                {isExpanded ? (
                  <>
                    {' '}
                    Expand All <FaCaretDown />
                  </>
                ) : (
                  <>
                    {' '}
                    Collapse All <FaCaretUp />
                  </>
                )}
              </Button>
            </div>
          </div>
        </>
      )}
      <div className="lineitem-grid">
        <ObGrid
          reactiveCustomComponents={true}
          columnDefs={ClaimLineItemColumnConfig as any}
          rowData={serviceLineItems}
          rowHeight={55}
          defaultColDef={{
            resizable: true,
            wrapHeaderText: true,
          }}
          pagination={false}
          gridOptions={{
            localeText: {
              noRowsToShow: 'No Service Content Extracted',
            },
          }}
          detailRowAutoHeight={true}
          masterDetail={true}
          detailCellRendererParams={detailCellRendererParams}
          onGridReady={onGridReady as any}
        />
      </div>
      <ObModal
        show={showModal}
        close={() => setShowModal(false)}
        title="Explanation of Benefits"
        size="xl"
        customSize="full-width"
        height="auto"
      >
        <ModalContent>
          <embed src={pdf} width="100%" height="670" type="application/pdf"></embed>
        </ModalContent>
      </ObModal>
    </Div>
  )
}

const Div = styled.div<{ panelHeight: number }>`
  display: flex;
  flex-direction: column;

  .extracted-details {
    min-height: 20px;
  }
  .lineitem-extration {
    .extracted-detail-label {
      ${({ theme }) => theme.typography.xxs.medium}!important;
      color: ${({ theme }) => theme.colors.grey.neutral}!important;
      margin-top: 0.5rem;
    }
    .extracted-detail-value {
      ${({ theme }) => theme.typography.xs.medium}!important;
      display: inline-flex;
      padding: 0.5rem;
      color: ${({ theme }) => theme.colors.primary.neutral};
      background-color: rgb(249, 249, 251);
      border-radius: 5px;
    }
  }

  .lineitem-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    margin-bottom: 0.5rem;

    .search-container {
      position: relative;
      display: flex;
      align-items: center;
      border: 1px solid #ccc;
      width: 350px;
      padding: 0 ${({ theme }) => theme.spacing.xs};
      border-radius: ${({ theme }) => theme.borderRadius.rounded};

      svg {
        color: ${({ theme }) => theme.colors.grey.light};
        cursor: pointer;
        ${({ theme }) => theme.typography.xs.normal};
      }
    }

    .search-container input {
      width: 100%;
      border: none;
      padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.xs};
      outline: none;
      ${({ theme }) => theme.typography.s.normal};
      color: ${({ theme }) => theme.colors.grey.neutral};
    }

    .search-container input:focus {
      outline: none;
    }

    .lineitem-settings {
      div {
        margin-top: 0.7rem;
        ${({ theme }) => theme.typography.s.normal};
        color: ${({ theme }) => theme.colors.grey.dark};
      }
      display: flex;
      gap: 0.8rem;
    }
  }

  .lineitem-grid {
    height: calc(100vh - ${({ panelHeight }) => panelHeight}px - 455px);

    .ag-details-row-auto-height {
      padding: 5px 25px 5px 75px;
      background-color: ${({ theme }) => theme.colors.neutral.lighter};
      .ag-details-grid-auto-height {
        border: 1px solid ${({ theme }) => theme.colors.grey.lighter}!important;
      }
      .ag-details-grid {
        border: none;
      }
      .ag-row {
        height: auto !important;
      }
      .ag-cell {
        padding-left: 10px;
      }
      .ag-cell-value {
        white-space: normal;
        word-break: break-word;
      }
    }
    .ag-floating-bottom-container {
      .ag-row-pinned {
        .ag-cell {
          background-color: ${({ theme }) => theme.colors.grey.lightest};
          ${({ theme }) => theme.typography.xs.bold};
          margin: 0;
        }
      }
    }
    .ag-floating-bottom {
      min-height: 39px !important;
    }

    .ag-header-cell {
      padding: 0 0 0 ${({ theme }) => theme.spacing.xs};
      overflow: visible;
      text-overflow: unset;
      white-space: normal;
      break-word: break-all;
    }
    .ag-header-cell-text {
      ${({ theme }) => theme.typography.xs.bold}!important;
    }
    .ag-row {
      border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    }
  }
`

const ModalContent = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  svg {
    font-size: 2.5rem;
    color: ${({ theme }) => theme.colors.tertiary.neutral};
    margin-bottom: 1rem;
  }
  .heading {
    font-size: 16px;
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }
  .content {
    ${({ theme }) => theme.typography.m.normal};
  }
`
